import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties } from "react";
import { FileDropZone } from "./FileDropZone";
import { useTranslation } from "react-i18next";
import {
  ATTACHMENT_KINDS,
  UPLOAD_KINDS,
  MAX_ATTACHMENT_BYTES,
  attachmentsApi,
  isImageContentType,
  type AttachmentEntityKind,
  type AttachmentKind,
  type AttachmentView,
} from "../api/attachments";
import type { ApiError } from "../api/client";
import { formatDateTime } from "../lib/dates";
import { formatBytes } from "../lib/format";
import { useAttachmentUpload } from "../lib/useAttachmentUpload";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "./Toast";
import { useConfirm } from "./ConfirmDialog";
import { Select, type SelectOption } from "./Select";
import { UploadRows, attachmentKindLabel } from "./UploadRows";
import Ic from "./icons.jsx";

const ALL_KINDS = "";
const TOUCH_QUERY = "(pointer: coarse), (max-width: 820px)";

/** True on touch devices or narrow screens — where a "take a photo" button makes sense. */
function useTouchOrNarrow(): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(TOUCH_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(TOUCH_QUERY);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return matches;
}

/**
 * Attachments of one entity (contract §6): card grid with thumbnail / icon,
 * kind chip, filter by kind, open in a new tab, delete, and the
 * presign → PUT → confirm upload flow with a per-file progress row.
 * On POSITION the drawings are listed first.
 */
export function AttachmentsPanel({
  entityKind, entityId, title, entityCode, allowedKinds,
}: {
  entityKind: AttachmentEntityKind;
  entityId: string;
  /** Card heading; defaults to "Prilozi". */
  title?: string;
  /** Shown under the heading (e.g. asset tag / position code). */
  entityCode?: string | null;
  /** Restrict upload kinds (e.g. a location only takes photos). One kind hides the selector. */
  allowedKinds?: AttachmentKind[];
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const canUpload = hasPermission("attachments:upload");
  const canDelete = hasPermission("attachments:delete");
  const canView = hasPermission("attachments:view");
  const touch = useTouchOrNarrow();

  const [items, setItems] = useState<AttachmentView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<AttachmentKind | typeof ALL_KINDS>(ALL_KINDS);
  const kinds = allowedKinds && allowedKinds.length > 0 ? allowedKinds : UPLOAD_KINDS;
  const [uploadKind, setUploadKind] = useState<AttachmentKind>(
    kinds.includes(entityKind === "POSITION" ? "DRAWING" : "PHOTO") ? (entityKind === "POSITION" ? "DRAWING" : "PHOTO") : kinds[0],
  );
  const photoOnly = kinds.every((k) => k === "PHOTO");
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const onUploaded = useCallback((view: AttachmentView) => {
    setItems((prev) => [view, ...(prev ?? [])]);
  }, []);
  // presign → PUT (raw fetch, no cookies) → confirm; the row disappears on success, stays red on failure.
  const upload = useAttachmentUpload(entityKind, entityId, onUploaded);
  const resetUploads = upload.reset;

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await attachmentsApi.list({ entityKind, entityId }));
    } catch (err) {
      setError((err as ApiError).message);
      setItems([]);
    }
  }, [entityKind, entityId]);

  useEffect(() => {
    setItems(null);
    resetUploads();
    setFilter(ALL_KINDS);
    if (!canView) return;
    void load();
  }, [load, resetUploads, canView]);

  // Drawings first on a position (contract §6); otherwise keep the server order (createdAt DESC).
  // Layout backgrounds are a separate category (module layouts) and never show up here.
  const sorted = useMemo(() => {
    const list = (items ?? []).filter((a) => a.kind !== "LAYOUT");
    if (entityKind !== "POSITION") return list;
    return [...list].sort((a, b) => Number(b.kind === "DRAWING") - Number(a.kind === "DRAWING"));
  }, [items, entityKind]);

  const kindsPresent = useMemo(
    () => ATTACHMENT_KINDS.filter((k) => sorted.some((a) => a.kind === k)),
    [sorted],
  );
  const visible = filter === ALL_KINDS ? sorted : sorted.filter((a) => a.kind === filter);

  const kindOptions = useMemo<SelectOption<AttachmentKind>[]>(
    () => kinds.map((k) => ({ value: k, label: attachmentKindLabel(t, k) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, kinds.join(",")],
  );

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-picking the same file
    // Sequential so the server numbers the codes (-P1, -P2…) in pick order.
    void upload.addFiles(files, uploadKind);
  }

  async function handleDelete(a: AttachmentView) {
    const ok = await confirm({
      title: t("attachments.confirmDelete.title", "Brisanje priloga"),
      message: t("attachments.confirmDelete.message", "Obrisati prilog „{{name}}“ ({{code}})?", { name: a.filename, code: a.code }),
      confirmLabel: t("action.delete", "Obriši"),
      danger: true,
    });
    if (!ok) return;
    try {
      await attachmentsApi.remove(a.id);
      setItems((prev) => (prev ?? []).filter((x) => x.id !== a.id));
      toast.success(t("attachments.toast.deleted", "Prilog obrisan."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  const heading = title ?? t("attachments.title", "Prilozi");

  // Photos stay images; the general panel takes any file (undefined = no filter).
  const acceptTypes = photoOnly ? "image/*" : undefined;

  // Without the view function the whole card stays out — the page does not need to know (audit 2026-09-23 §C).
  if (!canView) return null;

  return (
    <FileDropZone onFiles={(files) => { void upload.addFiles(files, uploadKind); }} accept={acceptTypes} disabled={!canUpload}>
    <section className="card">
      <div className="card-h">
        <div style={{ minWidth: 0 }}>
          <h3>{heading}</h3>
          <div className="sub">
            {entityCode && <span className="mono">{entityCode} · </span>}
            {items === null
              ? t("common.loading", "Učitavanje…")
              : t("attachments.count", "{{count}} priloga", { count: items.length })}
          </div>
        </div>
        {kindsPresent.length > 1 && (
          <div className="chip-group" role="group" aria-label={t("attachments.filter", "Filter po vrsti")}>
            <button type="button" className={`chip${filter === ALL_KINDS ? " active" : ""}`} aria-pressed={filter === ALL_KINDS} onClick={() => setFilter(ALL_KINDS)}>
              {t("attachments.filterAll", "Sve")}
            </button>
            {kindsPresent.map((k) => (
              <button key={k} type="button" className={`chip${filter === k ? " active" : ""}`} aria-pressed={filter === k} onClick={() => setFilter(k)}>
                {attachmentKindLabel(t, k)}
              </button>
            ))}
          </div>
        )}
      </div>

      {canUpload && (
        <div className="card-b" style={{ paddingBottom: 0 }}>
          <div className="att-upload">
            {kinds.length > 1 && (
              <Select<AttachmentKind>
                value={uploadKind}
                options={kindOptions}
                onChange={setUploadKind}
                ariaLabel={t("attachments.field.kind", "Vrsta priloga")}
                style={{ flex: "0 1 200px", minWidth: 160 }}
              />
            )}
            <input
              ref={fileRef}
              type="file"
              accept={acceptTypes}
              multiple
              hidden
              aria-label={t("attachments.add", "Dodaj prilog")}
              onChange={onPick}
            />
            <button type="button" className="btn primary" onClick={() => fileRef.current?.click()}>
              <Ic.upload /> {t("attachments.add", "Dodaj prilog")}
            </button>
            {touch && (
              <>
                <input
                  ref={cameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  aria-label={t("attachments.takePhoto", "Slikaj")}
                  onChange={onPick}
                />
                <button type="button" className="btn" onClick={() => cameraRef.current?.click()}>
                  <Ic.image /> {t("attachments.takePhoto", "Slikaj")}
                </button>
              </>
            )}
            <span className="att-hint muted">
              {photoOnly
                ? t("attachments.hintPhoto", "JPEG, PNG ili WebP, do {{max}} MB po fajlu — ili prevucite fajlove ovde.", { max: Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024) })
                : t("attachments.hint", "Bilo koji fajl (slika, PDF, Word, Excel, crtež…), do {{max}} MB po fajlu — ili prevucite fajlove ovde.", { max: Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024) })}
            </span>
          </div>
          <UploadRows uploads={upload.uploads} onRetry={(u) => { void upload.retry(u); }} onDismiss={upload.dismiss} />
        </div>
      )}

      {error ? (
        <div style={{ ...errorBox, margin: 18 }} role="alert">{error}</div>
      ) : items === null ? (
        <div className="empty">{t("common.loading", "Učitavanje…")}</div>
      ) : visible.length === 0 ? (
        <div className="empty">
          {items.length === 0
            ? t("attachments.empty", "Još nema priloga.")
            : t("attachments.emptyFiltered", "Nema priloga te vrste.")}
        </div>
      ) : (
        <div className="card-b">
          <div className="att-grid">
            {visible.map((a) => {
              // Same-origin app routes that 302 to a fresh presigned URL — never expire in the UI.
              const openUrl = `/api/attachments/${a.id}/content?disposition=inline`;
              const image = isImageContentType(a.contentType);
              return (
                <article key={a.id} className="att-card" aria-label={a.filename}>
                  {openUrl ? (
                    <a className="att-thumb" href={openUrl} target="_blank" rel="noopener noreferrer" title={t("attachments.open", "Otvori u novom tabu")}>
                      <Thumb a={a} image={image} />
                    </a>
                  ) : (
                    <div className="att-thumb"><Thumb a={a} image={image} /></div>
                  )}
                  <div className="att-body">
                    <div className="att-tags">
                      <span className={`tag${a.kind === "DRAWING" ? " info" : ""}`}>{attachmentKindLabel(t, a.kind)}</span>
                      <span className="mono muted" style={{ fontSize: 11.5 }}>{a.code}</span>
                    </div>
                    <div className="att-name" title={a.filename}>{a.filename}</div>
                    <div className="att-sub muted">
                      {formatBytes(a.sizeBytes)} · {a.uploadedByName} · <time dateTime={a.createdAt} title={formatDateTime(a.createdAt)}>{formatDateTime(a.createdAt)}</time>
                    </div>
                    <div className="att-actions">
                      {openUrl && (
                        <a className="btn sm" href={openUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                          <Ic.eye /> {t("attachments.openShort", "Otvori")}
                        </a>
                      )}
                      {canDelete && (
                        <button type="button" className="btn sm" style={dangerBtn} onClick={() => handleDelete(a)}>
                          {t("action.delete", "Obriši")}
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}
    </section>
    </FileDropZone>
  );
}

function Thumb({ a, image }: { a: AttachmentView; image: boolean }) {
  if (a.thumbnailUrl) return <ThumbImg id={a.id} />;
  return image ? <Ic.image aria-hidden="true" /> : <Ic.pdf aria-hidden="true" />;
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };

/**
 * Thumbnail via the app redirect route. The preview is generated asynchronously a moment
 * after confirm, so a freshly uploaded image may 404 once or twice — retry with a cache
 * buster before giving up and showing the generic icon.
 */
function ThumbImg({ id }: { id: string }) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [id]);
  if (failed) return <Ic.image aria-hidden="true" />;
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError is a load hook, not interaction
    <img
      src={`/api/attachments/${id}/thumbnail${attempt > 0 ? `?r=${attempt}` : ""}`}
      alt=""
      loading="lazy"
      onError={() => {
        if (attempt < 4) setTimeout(() => setAttempt((n) => n + 1), 1500 * (attempt + 1));
        else setFailed(true);
      }}
    />
  );
}
